import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getPreSignedUrl,
  patchUserProfilePic,
} from 'src/redux/services/commonServices';
import { setModalContent } from 'src/redux/modules/modalEvent';
import ConfirmationModalContainer from 'src/containers/ConfirmationModal/ConfirmationModalContainer';
import {
  updateContinueButton,
  updatedProfilePic,
} from 'src/redux/actions/questionnaireActions';
import { setFlashNotification } from 'src/redux/modules/flashNotification';
import { updateLoggedInUser } from 'src/redux/modules/authentication';
import { OptionDropdown } from './OptionDropdown';
import { allowedFileTypes, invalidFileExtensions } from './constant';
import { UploadImageContainer } from './UploadImageContainer';
import { updateUserToBeOnboarded } from 'src/redux/actions/onboardingActions';
import './photo.scss';
import { User } from '../UserProfileHeader/types';
import { noop } from 'lodash';
import { Typography } from 'src/latest/src/components';
import { DocumentType } from 'src/latest/src/constants/DocumentTypeEnum';

type Props = {
  questionData?: any;
  onChange?: (srcPic: string, choiceId?: string) => void;
  setUploading?: (status: boolean) => void;
  userDetails: User;
  isIntakeQuestionnaire?: boolean;
  size: 'm' | 'l';
  onUploadError?: (error: string) => void;
  documentType?: DocumentType | undefined;
};

export const Photo = ({
  questionData,
  onChange = noop,
  setUploading,
  userDetails,
  isIntakeQuestionnaire,
  // onUploadError = () => {},
  size,
  documentType,
}: Props) => {
  const dispatch = useDispatch();
  const choice = questionData?.choices[0];
  const { loggedInUser } = useSelector(
    ({ authentication }: any) => authentication,
  );
  const [user, setUser] = useState<any>({});
  const [decodedImageSrc, setDecodedImageSrc] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const profilePic: any = useRef(null);

  const [isLoggedinUserProfile, setIsLoggedinUserProfile] =
    useState<boolean>(false);
  const userToBeOnboarded = useSelector(
    (state: any) => state?.onboarding?.userToBeOnboarded,
  );

  useEffect(() => {
    if (userDetails) {
      setUser(userDetails);
      _initData(userDetails);
      setIsLoggedinUserProfile(userDetails?.id === loggedInUser?.id);
    }
  }, [userDetails]);

  const onProfileChangeClick = () => {
    // `current` points to the mounted file input element
    if (profilePic?.current) {
      profilePic.current.click();
    }
  };

  const isDirectoryPlatform = useSelector(
    (state: any) => state?.configuration?.isDirectoryPlatform === 'true',
  );

  const toggleUploadingStatus = (status: boolean) => {
    setIsUploading(status);

    if (setUploading) {
      setUploading(status);
    }
  };

  const _initData = (currentUser: any = null) => {
    const imageSource =
      documentType === DocumentType.OFFICE_PIC
        ? choice?.value
        : currentUser?.profile_pic;

    if (imageSource) {
      (async () => {
        const srcPic = await decodeURIComponent(imageSource);

        if (srcPic === '' || srcPic === null || srcPic === undefined) {
          toggleUploadingStatus(false);
          setDecodedImageSrc(null);
        } else {
          toggleUploadingStatus(false);
          setDecodedImageSrc(srcPic);

          if (isIntakeQuestionnaire && documentType !== DocumentType.OFFICE_PIC) {
            onChange(srcPic, choice?.id);
          }
        }
      })();
    }
  };

  const onSetValue = async (e: any) => {
    if (e.target.files[0] === undefined || !user?.id) {
      return;
    }

    if (e && e.target && e.target.files[0]) {
      const file = e.target.files[0];
      const { type } = file;
      const fileExtension = file?.name?.split('.').pop().toLowerCase();

      if (
        !allowedFileTypes.includes(type) ||
        invalidFileExtensions.includes(fileExtension)
      ) {
        handleUploadError('Uploaded file is not a valid image file.');
        e.target.value = '';

        return;
      }

      // REMOVED SIZE RESTRICTION FOR UPLOADING PROFILE PICTURE AS PER REQUEST FROM PO, PLEASE UNCOMMENT THE BELOW CODE TO ENABLE THE SIZE RESTRICTION BACK

      // if (size > 1e6 && !isMobileDevice()) {
      //   handleUploadError('File size exceeds maximum limit (1MB).');
      //   e.target.value = '';

      //   return;
      // }

      toggleUploadingStatus(true);
      setDecodedImageSrc(null);

      // NEW DOCUMENT SERVICE API CALLS
      // ======================================================
      const preSignedUrl = await getPreSignedUrl(
        window.location.pathname.includes('settings')
          ? DocumentType.PROFILE_PIC
          : documentType,
        e.target.files[0],
        user?.id,
        user?.id,
        !isIntakeQuestionnaire,
      );

      // =====================================================

      if (preSignedUrl === '') {
        dispatch(
          setFlashNotification({
            message:
              'There was an error uploading Profile picture please try again',
            isError: true,
            duration: 3000,
          }),
        );
        toggleUploadingStatus(false);

        return;
      }

      if (documentType === DocumentType.OFFICE_PIC && isDirectoryPlatform) {
        toggleUploadingStatus(false);

        if (!window.location.pathname.includes('settings')) {
          onChange(preSignedUrl, choice?.id);
        }

        if (file) {
          const localPreviewUrl = URL.createObjectURL(file);

          setDecodedImageSrc(localPreviewUrl);
        }

        e.target.value = '';

        return;
      }

      const srcPic = await decodeURIComponent(preSignedUrl);

      setDecodedImageSrc(srcPic);
      toggleUploadingStatus(false);

      isLoggedinUserProfile &&
        dispatch(
          updateLoggedInUser({
            ...user,
            profile_pic: preSignedUrl || '',
          }),
        );

      if (!window.location.pathname.includes('settings')) {
        onChange(preSignedUrl, choice?.id);
      }

      if (
        userToBeOnboarded?.id === userDetails?.id &&
        documentType === DocumentType.PROFILE_PIC
      ) {
        dispatch(updateUserToBeOnboarded(preSignedUrl || ''));
      }
    } else if (e) {
      setDecodedImageSrc('');
    }

    e.target.value = '';
  };

  const uploadEmptyPic = async () => {
    if (user?.id) {
      toggleUploadingStatus(true);
      dispatch(setModalContent({ modalContent: null }));
      await patchUserProfilePic('null', user?.id, user?.id);
      dispatch(updateContinueButton({ buttonStatus: 'disabled' }));
      toggleUploadingStatus(false);
      setDecodedImageSrc('');
      dispatch(updatedProfilePic('removed'));

      if (userToBeOnboarded?.id === userDetails?.id) {
        dispatch(updateUserToBeOnboarded(''));
      }

      isLoggedinUserProfile &&
        dispatch(updateLoggedInUser({ ...user, profile_pic: '' }));
      dispatch(updatedProfilePic(Math.random().toString()));
    }
  };

  const removeImage = () => {
    if (!isIntakeQuestionnaire) {
      dispatch(
        setModalContent({
          modalContent: (
            <div>
              <ConfirmationModalContainer
                clickedYes={uploadEmptyPic}
                clickedOnNo={() =>
                  dispatch(
                    setModalContent({
                      title: null,
                      modalContent: null,
                    }),
                  )
                }
                dynamicText={
                  'Are you sure you want to delete the profile photo?'
                }
              />
            </div>
          ),
          size: 'lg',
          isWithCancelButton: false,
          isWithCloseButton: true,
          isGeneralPopup: true,
          title: 'Delete profile photo?',
          className: 'confirmation-modal',
        }),
      );
    } else {
      onChange('', choice?.id);

      if (documentType === DocumentType.OFFICE_PIC) {
        setDecodedImageSrc('');
      } else {
        uploadEmptyPic();
      }
    }
  };

  const handleUploadError = (error: string) => {
    dispatch(
      setFlashNotification({
        message: error,
        isError: true,
      }),
    );
  };

  return (
    <div className="photo-container">
      <div
        className={`photo-container-content ${
          isUploading && `border-${size}`
        } size-${size}`}
      >
        <input
          onChange={onSetValue}
          type="file"
          accept="image/png, image/jpeg"
          className="d-none"
          id="profile-pic-file"
          ref={profilePic}
        />
        {decodedImageSrc ? (
          <>
            <OptionDropdown
              onProfileChangeClick={onProfileChangeClick}
              removeImage={removeImage}
              size={size}
              questionData={questionData}
              isDirectoryPlatform={isDirectoryPlatform}
            />
            <img
              id="img"
              alt="User"
              className="photo-container-profile-img mx-auto d-block"
              src={decodedImageSrc}
              onClick={onProfileChangeClick}
            />
          </>
        ) : (
          <>
            {!isUploading && (
              <UploadImageContainer
                onUploadProfileClick={onProfileChangeClick}
              />
            )}
            <Typography
              color="color-28"
              variant="size-16"
              weight="semi-bold"
              className="uploading-text"
            >
              {isUploading && 'Uploading ...'}
            </Typography>
          </>
        )}
      </div>
    </div>
  );
};
